import { FC, memo, useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Upload
} from 'antd';
import { PERMISSION_ROUTES, PERMISSION_ACTIONS } from 'constants/permissions';
import { useFileUpload, usePermission } from 'hooks';
import dayjs, { Dayjs } from 'dayjs';
import { UploadOutlined } from '@ant-design/icons';
import { ATTRIBUTE_PLACE_USE } from 'constants/attributes';
import { RTL_LANGUAGES } from 'constants/translations';
import { fetchAttributesPlaceUse } from 'services/api/attributes';
import { createUserAttributes } from 'services/api/userGroup';
import { Attribute } from 'types/api/attributes';

import { CKEditor, Private } from 'components/shared';
import { StyledPage } from './styled';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Loading } from 'components/ui';

interface IProps {
  id: string;
  lang: string;
  allowToEdit?: boolean;
}

const DynamicPage: FC<IProps> = ({ id, lang, allowToEdit }) => {
  const { message } = App.useApp();
  const { checkUserPermission } = usePermission();
  const { upload } = useFileUpload({
    progress: true
  });

  const [form] = Form.useForm();

  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsFetching(true);
        const res = await fetchAttributesPlaceUse([
          ATTRIBUTE_PLACE_USE.report,
          ATTRIBUTE_PLACE_USE.doctorProfile,
          ATTRIBUTE_PLACE_USE.email
        ]);

        if (!cancelled) setAttributes(res.data ?? []);
      } catch {
        // swallow — empty form on failure
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const hasProfileUpdatePermission = checkUserPermission(
    PERMISSION_ROUTES.doctors,
    PERMISSION_ACTIONS.update
  );

  const renderField = (item: Attribute) => {
    const fieldName = item.id;

    switch (item.type) {
      case 'date':
        return (
          <Form.Item name={fieldName} label={item.name}>
            <DatePicker style={{ width: '100%' }} disabled={!allowToEdit} />
          </Form.Item>
        );
      case 'editor':
        return (
          <Form.Item name={fieldName} label={item.name}>
            <CKEditor disabled={!allowToEdit} />
          </Form.Item>
        );
      case 'file':
        return (
          <Form.Item
            name={fieldName}
            label={item.name}
            valuePropName="fileList"
            getValueFromEvent={e => e?.fileList || []}
          >
            <Upload
              disabled={!hasProfileUpdatePermission || !allowToEdit}
              maxCount={1}
              customRequest={e =>
                upload(e.file as File, {
                  onUploadProgress: e.onProgress
                })
                  .then(e.onSuccess)
                  .catch(e.onError)
              }
            >
              <Button disabled={!allowToEdit} block icon={<UploadOutlined />}>
                Upload
              </Button>
            </Upload>
          </Form.Item>
        );
      default:
        return (
          <Form.Item name={fieldName} label={item.name}>
            <Input
              dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
              disabled={!allowToEdit}
            />
          </Form.Item>
        );
    }
  };

  const onFinish = async (data: Record<string, string | Dayjs>) => {
    const values: Record<string, string | undefined> = {};

    Object.entries(data).forEach(([key, value]) => {
      if (dayjs.isDayjs(value)) {
        values[key] = value.format();

        return;
      }

      if (Array.isArray(value)) {
        values[key] = value?.[0]?.response || undefined;

        return;
      }

      values[key] = value;
    });

    try {
      setIsCreating(true);
      await createUserAttributes({
        user_id: Number(id),
        lang,
        values
      });
      message.success('Successfully saved');
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsCreating(false);
    }
  };

  const initialValues = useMemo(() => {
    // if (doctorAttributeGroups?.attributes?.length) {
    //   const values = doctorAttributeGroups.attributes.reduce<
    //     Record<string, string | Dayjs | unknown[]>[]
    //   >((acc, item) => {
    //     const index = item.row_index - 1;

    //     acc[index] = {
    //       ...(acc[index] || {}),
    //       [item.attribute_id]:
    //         item.type === 'date' ? dayjs(item.value) : item.value
    //     };

    //     if (!acc[index]) {
    //       acc[index] = {};
    //     }

    //     switch (item.type) {
    //       case 'date':
    //         acc[index][item.attribute_id] = dayjs(item.value);
    //         break;
    //       case 'file':
    //         acc[index][item.attribute_id] = [
    //           {
    //             uid: item.value,
    //             name: item.value,
    //             response: item.value
    //           }
    //         ];
    //         break;
    //       default:
    //         acc[index][item.attribute_id] = item.value;
    //         break;
    //     }

    //     return [...acc];
    //   }, []);

    //   return values;
    // }

    return [{}];
  }, []);

  if (isFetching) {
    return <Loading />;
  }

  return (
    <StyledPage>
      <Form
        form={form}
        layout="vertical"
        size="large"
        disabled={!hasProfileUpdatePermission}
        onFinish={onFinish}
        initialValues={{
          values: initialValues
        }}
      >
        <Row gutter={[16, 0]}>
          {attributes.map(item => (
            <Col span={24} key={`${item.id}`}>
              {renderField(item)}
            </Col>
          ))}
        </Row>
        <Flex justify="end" gap={12}>
          <Private
            route={PERMISSION_ROUTES.doctors}
            action={PERMISSION_ACTIONS.update}
          >
            <Button
              size="large"
              type="primary"
              onClick={form.submit}
              loading={isCreating}
              disabled={!allowToEdit}
            >
              Save
            </Button>
          </Private>
        </Flex>
      </Form>
    </StyledPage>
  );
};

export default memo(DynamicPage);
